import type { Query } from "@monolith-forensics/monolith-ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { validate as isUuid } from "uuid";

import { hasDataArray, isConnectionFailure } from "@/api/connection.types";
import { labAssetQueryKeys } from "@/Features/LabAssets/labAssets.queryKeys";

import { AssetAttachmentsAPI } from "./asset-attachments.api";
import { assetAttachmentQueryKeys } from "./asset-attachments.keys";
import type {
  AssetAttachment,
  QueryAssetAttachmentsParams,
} from "./asset-attachments.types";

/**
 * Every report attached to one validation (ENG-1673), in upload order (the
 * api's default `id ASC`). A single page: one validation never has more
 * than a handful. No request for a missing or malformed uuid; a connection
 * failure throws so the section can render its own error line.
 * `refetchOnMount: "always"` re-signs the presigned `file_url`s on every open.
 */
export const useValidationReports = (validation_uuid?: string) => {
  const query: QueryAssetAttachmentsParams = {
    validation_uuid,
    page_size: 1000,
  };
  return useQuery({
    queryKey: assetAttachmentQueryKeys.list(query),
    queryFn: async (): Promise<AssetAttachment[]> => {
      const result: unknown = await AssetAttachmentsAPI.get({
        ...query,
        skipErrorToast: true,
      });
      if (!hasDataArray(result)) {
        throw new Error(`Failed to load reports for ${validation_uuid}`);
      }
      return result.data as AssetAttachment[];
    },
    enabled: !!validation_uuid && isUuid(validation_uuid),
    retry: false,
    refetchOnMount: "always",
  });
};

// General attachments only: reports carry a validation_uuid. The top-level
// `validation_uuid` param takes a uuid string only, so the null match has to
// go through a filter rule. The api's Zod schema is strict about this shape
// (non-empty labels, listed operator values); a malformed rule 400s.
export const ASSET_ONLY_FILTER: Query = {
  combinator: "and",
  rules: [
    {
      dataField: "validation_uuid",
      operator: { label: "is null", value: "isNull" },
      label: "Validation",
    },
  ],
};

/**
 * An asset's general attachments (ENG-1671), reports excluded, in upload
 * order. Same single-page shape and options as useValidationReports.
 */
export const useAssetAttachments = (asset_uuid?: string) => {
  const query: QueryAssetAttachmentsParams = {
    asset_uuid,
    page_size: 1000,
    filter: ASSET_ONLY_FILTER,
  };
  return useQuery({
    queryKey: assetAttachmentQueryKeys.list(query),
    queryFn: async (): Promise<AssetAttachment[]> => {
      const result: unknown = await AssetAttachmentsAPI.get({
        ...query,
        skipErrorToast: true,
      });
      if (!hasDataArray(result)) {
        throw new Error(`Failed to load attachments for ${asset_uuid}`);
      }
      return result.data as AssetAttachment[];
    },
    enabled: !!asset_uuid && isUuid(asset_uuid),
    retry: false,
    refetchOnMount: "always",
  });
};

// Upload and delete refetch every attachment list and the asset's History
// feed (the api writes an "Asset Attachment Added/Deleted" entry for each).
// History matters because an upload can finish after the user switched to
// that tab. Returned so the mutation resolves only once the list holds the
// change (no gap between the pending row leaving and the real row arriving).
const useInvalidateAttachments = (asset_uuid: string) => {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: assetAttachmentQueryKeys.all }),
      queryClient.invalidateQueries({
        queryKey: labAssetQueryKeys.activityLog(asset_uuid),
      }),
    ]);
};

export type UploadAssetAttachmentVariables = {
  file: File;
  onUploadProgress?: (percent: number) => void;
};

/**
 * Create → PUT → complete for one general attachment. AssetAttachmentsAPI
 * .upload already throws a plain Error naming the failed step; the caller
 * owns the error message.
 */
export const useUploadAssetAttachment = (asset_uuid: string) => {
  const invalidate = useInvalidateAttachments(asset_uuid);
  return useMutation<AssetAttachment, Error, UploadAssetAttachmentVariables>({
    mutationFn: ({ file, onUploadProgress }) =>
      AssetAttachmentsAPI.upload(file, {
        asset_uuid,
        onUploadProgress,
        skipErrorToast: true,
      }),
    onSuccess: invalidate,
  });
};

const GENERIC_DELETE_ERROR =
  "An error occurred while deleting this attachment.";

/**
 * DELETE /asset-attachments/:uuid. connection.js resolves failures as
 * { success: false }, so they are re-thrown with the server's message. A 404
 * means the row is already gone and counts as a success.
 */
export const useDeleteAssetAttachment = (asset_uuid: string) => {
  const invalidate = useInvalidateAttachments(asset_uuid);
  return useMutation<void, Error, string>({
    mutationFn: async (uuid) => {
      const result: unknown = await AssetAttachmentsAPI.delete(uuid, {
        skipErrorToast: true,
      });
      if (
        isConnectionFailure(result) &&
        result.error?.response?.status !== 404
      ) {
        throw new Error(
          result.error?.response?.data?.message || GENERIC_DELETE_ERROR
        );
      }
    },
    onSuccess: invalidate,
  });
};
